import React, { useContext, useEffect, useState } from 'react'

import SPage, { SPageType } from 'components/opinionless/SPage'
import { useTranslation } from '../../i18n'

import { useRouter } from 'next/router'
import { useAuth } from 'v2/hooks/useAuth'
import { UserContext } from 'v2/context/user'
import { useProtectedRoute } from 'utils/authentication'
import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'

import { useSTheme } from 'styles/theme'

import SBox from 'components/opinionless/SBox'

import HomeRecentPayments from './RecentPayments'
import { useMeQuery, useMyUpcomingPaymentLazyQuery, useSurveyAvailableLazyQuery } from '../../types/graphql'
import PageHeader from '../../components/PageHeader'
import { useMemo } from 'react'

import moment from 'moment-timezone'
import SFlex from 'components/opinionless/SFlex'
import HomePaymentMethod from './PaymentMethod'
import HomeUpcomingPayment from './UpcomingPayment'
import SButton, { SButtonType } from 'components/opinionless/SButton'
import SLink from 'components/opinionless/SLink'
import ConditionalRender from 'utils/conditionalRender'

interface Props {}

const HomeContainer: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)
    const { data, loading } = useMeQuery()
    const router = useRouter()

    const { loginLoading } = useAuth()
    const { authenticatedUser } = useContext(UserContext)

    const theme = useSTheme()
    const [surveyURL, setSurveyURL] = useState('#')
    const [surveyAvailableData, setSurveyAvailableData] = useState(null)
    const [upcomindD, setUpcomingD] = useState(null)
    const [isModalVisible, setIsModalVisible] = useState(false)

    const typeformId = window.ORGANIZATION_CONFIGS?.typeformId
    const typeformSubdomain = window.ORGANIZATION_CONFIGS?.typeformSubdomain
    const { authCheckComplete } = useProtectedRoute()
    const paymentProfileComplete =
        (authenticatedUser && authenticatedUser.transferMethods.edges.length) ||
        (authenticatedUser && authenticatedUser.isSuperuser)

    const surveyButtonDisabled =
        (!window.ORGANIZATION_CONFIGS.isTypeformBound || !surveyAvailableData?.surveyAvailable) &&
        !window.location.href.includes('longbeach')

    const [getSurveyAvailable, { data: sAData }] = useSurveyAvailableLazyQuery({
        pollInterval: 3000,
        fetchPolicy: 'no-cache',
    })

    useEffect(() => {
        if (authenticatedUser && window.ORGANIZATION_CONFIGS.isTypeformBound) {
            if (sAData) {
                setSurveyAvailableData(sAData)
            } else {
                getSurveyAvailable()
            }
        }
    }, [authenticatedUser, sAData])

    const onSurveyClick = (event) => {
        if (surveyButtonDisabled) {
            event.preventDefault()
            event.stopPropagation()
            return false
        }
    }

    useEffect(() => {
        if (localStorage.getItem('isUserFromSignUp')) {
            const handler = setTimeout(() => {
                localStorage.removeItem('isUserFromSignUp')
            }, 5000)
            setIsModalVisible(true)

            return () => {
                clearTimeout(handler)
            }
        }
    }, [])

    useEffect(() => {
        if (window.ORGANIZATION_CONFIGS.isTypeformBound && authenticatedUser) {
            const email = encodeURIComponent(authenticatedUser.email)
            setSurveyURL(
                `https://${typeformSubdomain}.typeform.com/to/${typeformId}/#email=${email}&user_id=${encodeURIComponent(
                    authenticatedUser.id
                )}`
            )
        }
    }, [authenticatedUser])

    // send them back to sign-up flow if they haven't selected a transfer method yet
    useEffect(() => {
        if (!loginLoading && authenticatedUser) {
            if (!paymentProfileComplete) {
                router.replace(
                    `/v2${window.POTENTIAL_USER?.program?.urlFragment || window.PROGRAM.urlFragment}/sign-up`
                )
            }
        }
    }, [loginLoading, paymentProfileComplete])

    const homeHeaderText = useMemo(() => {
        if (!data) {
            return null
        } else {
            const currentHour = moment().hour()

            if (currentHour >= 4 && currentHour < 12) {
                return t('home_header_morning', { name: data.me.firstName })
            } else if (currentHour >= 12 && currentHour < 18) {
                return t('home_header_afternoon', { name: data.me.firstName })
            } else {
                return t('home_header_evening', { name: data.me.firstName })
            }
        }
    }, [data, t])

    const [getUpcomingPayment, { data: upcomingData, loading: upcomingLoading }] = useMyUpcomingPaymentLazyQuery()

    useEffect(() => {
        if (authenticatedUser) {
            if (upcomingData) {
                setUpcomingD(upcomingData)
            } else {
                getUpcomingPayment()
            }
        }
    }, [authenticatedUser, upcomingData])

    const upcomingPaymentDateStr = useMemo(() => {
        if (upcomindD && upcomindD.myUpcomingPayment) {
            const scheduledDate = moment(upcomindD.myUpcomingPayment.scheduledDate).tz(
                window.ORGANIZATION_CONFIGS.timezone
            )
            if (scheduledDate.isSame(new Date(), 'day')) {
                return `${scheduledDate.format('MMM D, YYYY')}(${t('today')})`
            }
            if (scheduledDate.isSame(moment().add(1, 'day'), 'day')) {
                return `${scheduledDate.format('MMM D, YYYY')}(${t('tomorrow')})`
            }
            return scheduledDate.format('MMMM D, YYYY')
        } else {
            return null
        }
    }, [upcomindD, t])

    return (
        <SPage
            pageType={authCheckComplete ? SPageType.Authed : SPageType.None}
            sx={{
                display: 'flex',
                flexDirection: 'row',
            }}
        >
            {!authCheckComplete || loading || !paymentProfileComplete ? (
                <SLoadingIndicator width="100%" height={'100%'} color={theme.colors.secondaryColor} />
            ) : (
                <>
                    <SBox flex="1 1 auto">
                        <PageHeader>{homeHeaderText}</PageHeader>

                        <SFlex flexDirection={['column', 'row', 'row']} alignItems="stretch" flex="0 0 auto">
                            {/* TO DO: Replace with hashmap */}
                            <ConditionalRender if={window && window.ORGANIZATION_CONFIGS.isTypeformBound}>
                                <SFlex
                                    sx={{
                                        flex: ['0 0 auto', '1 1 0', '1 1 0'],
                                        borderRadius: [0, 2, 2],
                                        boxShadow: 'main',
                                        bg: 'white',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: [0, 8, 8],
                                        mb: '20px',
                                        padding: '16px 24px',
                                    }}
                                >
                                    <span>Take this month&apos;s survey to receive your payment!</span>
                                    <SLink
                                        href={surveyURL}
                                        target="_blank"
                                        onClick={onSurveyClick}
                                        sx={{
                                            cursor: surveyButtonDisabled ? 'default' : 'pointer',
                                        }}
                                    >
                                        <SButton
                                            sx={{
                                                bg: !surveyButtonDisabled ? 'backgroundColor' : 'gray200',
                                                '&:hover': {
                                                    cursor: surveyButtonDisabled ? 'default' : 'pointer',
                                                    bg: 'hoverColor',
                                                },
                                                lineHeight: '45px',
                                                overflow: 'hidden',
                                            }}
                                            textStyle={{
                                                color: !surveyButtonDisabled ? 'secondaryColor' : 'gray300',
                                            }}
                                            disabled={surveyButtonDisabled}
                                            buttonType={SButtonType.Outline}
                                        >
                                            Take Survey
                                        </SButton>
                                    </SLink>
                                </SFlex>
                            </ConditionalRender>
                        </SFlex>
                        <SFlex flexDirection={['column', 'row', 'row']} alignItems="stretch" flex="0 0 auto">
                            <HomePaymentMethod mb={[6, 9, 9]} mr={[0, 9, 9]} />
                            <HomeUpcomingPayment mb={[6, 9, 9]} />
                        </SFlex>
                        <HomeRecentPayments />
                    </SBox>
                </>
            )}
        </SPage>
    )
}

export default HomeContainer
